import { onUnmounted, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { getWindow } from '@/helpers/globals'
import { useIntercomSettings } from './useIntercomSettings'
import { useIntercom } from '@/plugins/intercom'

export interface IntercomLauncher {
    appId: string | undefined

    isLoaded: Ref<boolean>
    unreadCount: Ref<number>
    chatStarted: Ref<boolean>

    show(): Promise<void>
    loadIntercom(): Promise<void>
}

export function useIntercomLauncher(): IntercomLauncher {
    const window = getWindow()

    const { appId, chatStarted, unreadCount, isLoaded, load, unload } =
        useIntercom()
    const { getIntercomSettings } = useIntercomSettings(appId)

    const startChat = () => {
        chatStarted.value = true
    }

    const stopChat = () => {
        chatStarted.value = false
    }

    async function loadIntercom(): Promise<void> {
        if (!window) {
            return
        }

        await load()

        window.Intercom('onShow', startChat)

        window.Intercom('onHide', stopChat)

        window.Intercom('onUnreadCountChange', (count: number) => {
            unreadCount.value = count
        })

        const intercomSettings = getIntercomSettings()

        window.Intercom('boot', intercomSettings)
    }

    async function show(): Promise<void> {
        try {
            await loadIntercom()
            window?.Intercom?.('show')
        } catch (e) {
            alert(e)
        }
    }

    const router = useRouter()
    router.afterEach(() => {
        if (isLoaded.value) {
            window?.Intercom?.('update', {
                last_request_at: new Date().getTime() / 1000,
            })
        }
    })

    onUnmounted(() => {
        unload()
    })

    return {
        appId,
        isLoaded,
        unreadCount,
        chatStarted,
        show,
        loadIntercom,
    }
}
